<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { user, pendingWebhookEvents, awaitingApprovalCount, refreshAwaitingApprovalCount } from '../stores/authStore';
  import { settingsStore } from '../stores/settingsStore.js';
  import Icon from './Icon.svelte';
  import TrialWarningBanner from './TrialWarningBanner.svelte';
  import CombinedUiNotification from './CombinedUiNotification.svelte';
  import OnboardingWizard from './OnboardingWizard.svelte';
  import { identifyUser } from '../utils/logrocket';
  import { isXeroReauthReason, shouldAutoRedirectToXero } from '../lib/xeroReauth.js';

  export let activeRoute = '';

  let xeroAuthenticated = false;
  let xeroAuthReason = '';
  let jobtreadConnected = false;
  let checkingStatus = true;
  let checkingJTStatus = true;
  let showSessionExpiredModal = false;
  let userInfo = null;
  let jobtreadOrgName = null;
  let canRunAutomaticXeroCheck = false;
  let xeroOrganisationName = null;
  let showCombinedUiNotification = false;
  let showOnboardingWizard = false;

  function hasSavedXeroIntegrationSettings(info) {
    const invoiceCode = String(info?.['xeroinvoice-code'] || '').trim();
    const expenseCode = String(info?.['xeroexpense-code'] || '').trim();
    return Boolean(
      invoiceCode &&
      invoiceCode !== 'BLANK' &&
      expenseCode &&
      expenseCode !== 'BLANK'
    );
  }

  // Hide the Direct Bill Import menu for specific users
  const DIRECT_BILL_HIDDEN_USERS = ['tester@elevate.net.nz'];
  $: hideDirectBill = DIRECT_BILL_HIDDEN_USERS.includes(
    ($user?.email || $user?.username || '').trim().toLowerCase()
  );

  async function checkXeroStatus() {
    try {
      checkingStatus = true;
      const response = await fetch('/api/xero/check-auth');
      const data = await response.json();
      
      if (response.ok && data.authenticated) {
        xeroAuthenticated = true;
        xeroAuthReason = '';
        if (typeof data.secondsRemaining === 'number') {
          console.log(`Xero token seconds remaining: ${data.secondsRemaining}`);
        }
        xeroOrganisationName = data.xeroOrganisationName || null;
        if (typeof data.pendingWebhookEvents === 'number') {
          pendingWebhookEvents.set(data.pendingWebhookEvents);
        }
      } else if (shouldAutoRedirectToXero(data, activeRoute)) {
        // Only once the user has opened a Xero function (Direct Bill, Xero to
        // JobTread, and the other sync screens). The menu stays put.
        xeroAuthenticated = false;
        xeroAuthReason = data.reason || '';
        console.log('Xero re-auth required, redirecting to auth:', data.reason);
        window.location.href = data.redirectUrl;
        return;
      } else {
        xeroAuthenticated = false;
        xeroAuthReason = data.reason || '';
      }
    } catch (error) {
      console.error('Error checking Xero status:', error);
      xeroAuthenticated = false;
      xeroAuthReason = '';
    } finally {
      checkingStatus = false;
    }
  }

  async function checkJobTreadStatus() {
    try {
      checkingJTStatus = true;
      const response = await fetch('/api/jt/check-auth');
      const data = await response.json();
      
      if (response.ok && data.authenticated) {
        jobtreadConnected = true;
        jobtreadOrgName = data.organizationName;
        showSessionExpiredModal = false;
      } else {
        jobtreadConnected = false;
        jobtreadOrgName = null;
        if (data.error && data.needsLogin) {
          console.log('Session expired, showing login modal');
          showSessionExpiredModal = true;
        }
        throw new Error(data.error || 'Failed to check JobTread status');
      }
    } catch (error) {
      console.error('Error checking JobTread status:', error);
      jobtreadConnected = false;
      jobtreadOrgName = null;
      
      if (error.response && error.response.status === 401) {
        showSessionExpiredModal = true;
      }
    } finally {
      checkingJTStatus = false;
    }
  }

  async function fetchUserInfo() {
    try {
      const response = await fetch('/api/user');
      if (response.ok) {
        const userData = await response.json();
        if (userData.success && userData.user) {
          userInfo = userData.user;
          console.log('User info loaded:', userInfo); // Debug log
          
          // Sync full user profile to auth store so all pages have country, etc.
          user.set(userData.user);
          
          // Update pending webhook events count
          if (typeof userData.pendingWebhookEvents === 'number') {
            console.log('📊 Setting pending webhook events:', userData.pendingWebhookEvents);
            pendingWebhookEvents.set(userData.pendingWebhookEvents);
          }
          
          // Initialize LogRocket and identify user when session is restored
          if (userInfo.username) {
            identifyUser(userInfo.username, {
              email: userInfo.email || userInfo.username
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  }

  async function handleLogout() {
    try {
      const response = await fetch('/api/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        settingsStore.resetSettings();
        user.set(null); // Clear the user from the store
        navigate('/login'); // Redirect to login page
      } else {
        console.error('Logout failed:', data.message);
        // Optionally, show an error message to the user
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Optionally, show an error message to the user
    }
  }

  function goToProcessInvoices() {
    navigate('/process-invoices');
  }

  function goToImportBills() {
    navigate('/import-bills-from-xero');
  }

  function goToSyncBillsFromJT() {
    navigate('/sync-bills-from-jobtread');
  }

  function goToDirectBillImport() {
    navigate('/direct-bill-import');
  }

  function redirectToLogin() {
    navigate('/login');
    showSessionExpiredModal = false;
  }

  // QuickBooks users run JobTread-only — Xero is dormant (no status checks, no Xero nav).
  // Auth check only needs JobTread; "Connected" also requires saved default account codes.
  $: isQuickBooks = userInfo?.accountingPlatform === 'quickbooks';
  $: canRunAutomaticXeroCheck = !isQuickBooks && jobtreadConnected;
  $: xeroSettingsSaved = hasSavedXeroIntegrationSettings(userInfo);
  // Fully ready for sync: OAuth token + default sales/expense codes.
  $: xeroConnected = xeroAuthenticated && xeroSettingsSaved;
  $: xeroSetupIncomplete = xeroAuthenticated && !xeroSettingsSaved;
  // Broken or expired link to a pinned org. Stay on the menu and point at Settings.
  $: xeroNeedsFix = !xeroAuthenticated && isXeroReauthReason(xeroAuthReason);
  // Xero sync screens stay clickable when the token needs repair, so opening
  // them is what sends the user through Xero — not the menu load itself.
  $: xeroFunctionsReady = jobtreadConnected && (xeroConnected || xeroNeedsFix);
  // Direct Bill upload uses the org's JobTread + saved Xero codes (slaved from
  // the owner). It must not require this member's own Xero OAuth token — that
  // only matters later when they sync a bill to Xero.
  $: directBillReady = jobtreadConnected && (isQuickBooks || xeroSettingsSaved);


  function proceedAfterNotification() {
    showCombinedUiNotification = false;
    continueStartup();
  }

  async function continueStartup() {
    await checkJobTreadStatus();

    if (canRunAutomaticXeroCheck) {
      await checkXeroStatus();
    } else {
      xeroAuthenticated = false;
      xeroAuthReason = '';
      checkingStatus = false;
    }
  }

  onMount(async () => {
    await fetchUserInfo();
    refreshAwaitingApprovalCount();

    const hasJobtreadKey = userInfo?.jobtread && userInfo.jobtread !== 'BLANK';
    // A user who has already been invited into an org by its owner (i.e. they hold
    // a membership with a non-owner role) is joining a fully configured org — they
    // must never see the setup wizard.
    const isInvitedMember = !!(userInfo?.orgId && userInfo?.orgRole && userInfo.orgRole !== 'owner');
    if (userInfo && !userInfo.onboardingWizardSeen && !hasJobtreadKey && !isInvitedMember) {
      showOnboardingWizard = true;
    } else if (userInfo && !userInfo.combinedUiNotificationSeen) {
      showCombinedUiNotification = true;
    } else {
      await continueStartup();
    }

    const intervalId = setInterval(async () => {
      await Promise.all([fetchUserInfo(), checkJobTreadStatus(), refreshAwaitingApprovalCount()]);
      if (canRunAutomaticXeroCheck) {
        await checkXeroStatus();
      } else {
        xeroAuthenticated = false;
        xeroAuthReason = '';
        checkingStatus = false;
      }
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  });
</script>

<div class="h-screen flex overflow-hidden bg-[#f8fafc]">

  <!-- Static sidebar for desktop -->
  <div class="hidden md:flex md:flex-shrink-0">
    <div class="flex flex-col w-64">
      <div class="flex flex-col h-0 flex-1 sidebar-gradient">
        <div class="flex-1 flex flex-col overflow-y-auto">
          <!-- Logo -->
          <div class="flex-shrink-0 w-full px-5 py-3 flex justify-center">
            <a href="/dashboard" class="cursor-pointer flex justify-center w-5/6">
              <img class="h-auto rounded-lg" src="/images/buildzilla-logo-sm.jpg" alt="Logo">
            </a>
          </div>
          
          <!-- Divider -->
          <div class="border-t border-white/10 mb-3"></div>

          <!-- Xero Connection Status (hidden for QuickBooks users) -->
          {#if !isQuickBooks}
          <div class="px-5 mb-1.5 flex justify-center">
            <div class="w-5/6">
            {#if checkingStatus && canRunAutomaticXeroCheck}
              <div class="flex items-center px-3 py-2 rounded-md bg-white/5 border-l-[3px] border-yellow-400">
                <span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-yellow-400 mr-2"></span>
                <span class="text-[0.875rem] text-gray-200">Checking Xero connection...</span>
              </div>
            {:else if xeroConnected}
              <div class="flex items-center px-3 py-2 rounded-md bg-white/5 border-l-[3px] border-emerald-400">
                <span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2"></span>
                <span class="text-[0.875rem] text-gray-200">Xero Connected</span>
              </div>
            {:else if xeroSetupIncomplete}
              <a href="/settings/xero" class="px-3 py-2 rounded-md bg-white/5 border-l-[3px] border-red-400 hover:bg-white/10 transition-colors block">
                <div class="flex items-center mb-0.5">
                  <span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-400 mr-2"></span>
                  <span class="text-[0.875rem] text-red-300 font-medium">Xero setup incomplete</span>
                </div>
                <span class="text-xs text-[#93B4E4] underline ml-[0.875rem]">Finish default codes in Settings</span>
              </a>
            {:else if !jobtreadConnected}
              <div class="flex items-center px-3 py-2 rounded-md bg-white/5 border-l-[3px] border-yellow-400">
                <span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-yellow-400 mr-2"></span>
                <span class="text-[0.875rem] text-gray-200">Xero check paused until JobTread is connected.</span>
              </div>
            {:else if xeroNeedsFix}
              <a href="/settings/xero" class="px-3 py-2 rounded-md bg-white/5 border-l-[3px] border-red-400 hover:bg-white/10 transition-colors block">
                <div class="flex items-center mb-0.5">
                  <span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-400 mr-2"></span>
                  <span class="text-[0.875rem] text-red-300 font-medium">Xero needs fixing</span>
                </div>
                <span class="text-xs text-[#93B4E4] underline ml-[0.875rem]">Open Settings to disconnect or reconnect</span>
              </a>
            {:else}
              <a href="/api/xero/login" class="flex items-center px-3 py-2 rounded-md bg-white/5 border-l-[3px] border-amber-400 hover:bg-white/10 transition-colors">
                <span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400 mr-2"></span>
                <span class="text-[0.875rem] text-amber-200">Xero not connected — reconnect</span>
              </a>
            {/if}
            </div>
          </div>
          {/if}

          <!-- JobTread Connection Status -->
          <div class="px-5 mb-8 flex justify-center">
            <div class="w-5/6">
            {#if checkingJTStatus}
              <div class="flex items-center px-3 py-2 rounded-md bg-white/5 border-l-[3px] border-yellow-400">
                <span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-yellow-400 mr-2"></span>
                <span class="text-[0.875rem] text-gray-200">Checking JobTread connection...</span>
              </div>
            {:else if jobtreadConnected}
              <div class="flex items-center px-3 py-2 rounded-md bg-white/5 border-l-[3px] border-emerald-400">
                <span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2"></span>
                <span class="text-[0.875rem] text-gray-200">JobTread Connected</span>
              </div>
            {:else}
              <div class="px-3 py-2 rounded-md bg-white/5 border-l-[3px] border-red-400">
                <div class="flex items-center mb-0.5">
                  <span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-400 mr-2"></span>
                  <span class="text-[0.875rem] text-red-300 font-medium">JobTread Disconnected</span>
                </div>
                <div class="ml-[0.875rem]">
                  <a 
                    href="/settings" 
                    class="text-[0.8125rem] text-[#93B4E4] hover:text-white underline"
                  >
                    Click here to Fix this
                  </a>
                </div>
              </div>
            {/if}
            </div>
          </div>

          <nav class="flex-1 px-3">
            <div class="space-y-3.5">
                {#if !hideDirectBill}
                <!-- One Step Sync -->
                <div class="w-full">
                  <div class="flex items-center px-3 mb-1.5">
                    <span class="text-[0.875rem] font-semibold text-gray-300/80 uppercase tracking-[0.09375rem]">One Step Sync</span>
                    <span class="ml-2 px-1.5 py-0.5 text-[0.625rem] font-bold uppercase leading-none bg-purple-500 text-white rounded">Preview</span>
                  </div>
                  <div class="space-y-0.5">
                    <button
                      on:click={goToDirectBillImport}
                      disabled={!directBillReady}
                      class="group flex items-center px-3 py-2 w-full text-[0.9375rem] font-medium rounded-lg transition-colors border-0
                        {activeRoute === '/direct-bill-import' ? 'bg-white/[0.12] border-l-[3px] border-l-[#5B8AC4] text-white' : 'text-gray-200 border-l-[3px] border-l-transparent hover:bg-white/[0.15] hover:text-white hover:border-l-[#5B8AC4]/50'}
                        {!directBillReady ? 'opacity-40 cursor-not-allowed' : ''}"
                    >
                      <Icon
                        name="upload"
                        className="mr-3 h-5 w-5 {activeRoute === '/direct-bill-import' ? 'text-[#93B4E4]' : 'text-[#7B8FA8]'}"
                      />
                      Direct Bill Import
                    </button>
                  </div>
                </div>
                {/if}

                {#if !isQuickBooks}
                <!-- JobTread to Xero -->
                <div class="w-full">
                  <div class="text-[0.875rem] font-semibold text-gray-300/80 uppercase tracking-[0.09375rem] px-3 mb-1.5">
                    JobTread to Xero
                  </div>
                  <div class="space-y-0.5">
                    <button
                      on:click={() => navigate('/import-from-jobtread')}
                      disabled={!xeroFunctionsReady}
                      class="group flex items-center px-3 py-2 w-full text-[0.9375rem] font-medium rounded-lg transition-colors border-0
                        {activeRoute === 'import-from-jobtread' ? 'bg-white/[0.12] border-l-[3px] border-l-[#5B8AC4] text-white' : 'text-gray-200 border-l-[3px] border-l-transparent hover:bg-white/[0.15] hover:text-white hover:border-l-[#5B8AC4]/50'}
                        {!xeroFunctionsReady ? 'opacity-40 cursor-not-allowed' : ''}"
                    >
                      <Icon
                        name="link"
                        className="mr-3 h-5 w-5 {activeRoute === 'import-from-jobtread' ? 'text-[#93B4E4]' : 'text-[#7B8FA8]'}"
                      />
                      Import Bills & Invoices
                    </button>
                  </div>
                </div>

                <!-- Xero to JobTread -->
                <div class="w-full">
                  <div class="text-[0.875rem] font-semibold text-gray-300/80 uppercase tracking-[0.09375rem] px-3 mb-1.5">
                    Xero to JobTread
                  </div>
                  <div class="space-y-0.5">
                    <button
                      on:click={goToImportBills}
                      disabled={!xeroFunctionsReady}
                      class="group flex items-center px-3 py-2 w-full text-[0.9375rem] font-medium rounded-lg transition-colors border-0
                        {activeRoute === '/import-bills-from-xero' ? 'bg-white/[0.12] border-l-[3px] border-l-[#5B8AC4] text-white' : 'text-gray-200 border-l-[3px] border-l-transparent hover:bg-white/[0.15] hover:text-white hover:border-l-[#5B8AC4]/50'}
                        {!xeroFunctionsReady ? 'opacity-40 cursor-not-allowed' : ''}"
                    >
                      <Icon
                        name="cloud-download"
                        className="mr-3 h-5 w-5 {activeRoute === '/import-bills-from-xero' ? 'text-[#93B4E4]' : 'text-[#7B8FA8]'}"
                      />
                      Import Bills from Xero
                    </button>
                  </div>
                </div>
                {/if}

              <!-- Reporting and Approval -->
              <div class="w-full">
                <div class="text-[0.875rem] font-semibold text-gray-300/80 uppercase tracking-[0.09375rem] px-3 mb-1.5">
                  Reporting and Approval
                </div>
                <div class="space-y-0.5">
                  <button
                    on:click={() => navigate('/cost-reporting')}
                    disabled={!jobtreadConnected}
                    class="group flex items-center px-3 py-2 w-full text-[0.9375rem] font-medium rounded-lg transition-colors border-0
                      {activeRoute === '/cost-reporting' ? 'bg-white/[0.12] border-l-[3px] border-l-[#5B8AC4] text-white' : 'text-gray-200 border-l-[3px] border-l-transparent hover:bg-white/[0.15] hover:text-white hover:border-l-[#5B8AC4]/50'}
                      {!jobtreadConnected ? 'opacity-40 cursor-not-allowed' : ''}"
                  >
                    <Icon
                      name="chart-bar"
                      className="mr-3 h-5 w-5 {activeRoute === '/cost-reporting' ? 'text-[#93B4E4]' : 'text-[#7B8FA8]'}"
                    />
                    Cost Reporting
                  </button>
                  <button
                    on:click={() => navigate('/approvals')}
                    class="group flex items-center px-3 py-2 w-full text-[0.9375rem] font-medium rounded-lg transition-colors border-0
                      {activeRoute === 'approvals' ? 'bg-white/[0.12] border-l-[3px] border-l-[#5B8AC4] text-white' : 'text-gray-200 border-l-[3px] border-l-transparent hover:bg-white/[0.15] hover:text-white hover:border-l-[#5B8AC4]/50'}"
                  >
                    <Icon
                      name="check-circle"
                      className="mr-3 h-5 w-5 {activeRoute === 'approvals' ? 'text-[#93B4E4]' : 'text-[#7B8FA8]'}"
                    />
                    Approvals
                    {#if $awaitingApprovalCount > 0}
                      <span class="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-orange-500 text-white text-xs font-semibold">
                        {$awaitingApprovalCount}
                      </span>
                    {/if}
                  </button>
                </div>
              </div>
              
              <!-- Sales CRM Button - Only for user ID 1 and 2 (Buildzilla admins) -->
              {#if userInfo && (userInfo.id === 1 || userInfo.id === 2)}
                <div class="w-full">
                  <button
                    on:click={() => navigate('/crm')}
                    class="group flex items-center px-3 py-2 w-full text-[0.9375rem] font-medium rounded-lg transition-colors border-0
                      {activeRoute === 'crm' ? 'bg-white/[0.12] border-l-[3px] border-l-[#5B8AC4] text-white' : 'text-gray-200 border-l-[3px] border-l-transparent hover:bg-white/[0.15] hover:text-white hover:border-l-[#5B8AC4]/50'}"
                  >
                    <Icon
                      name="users"
                      className="mr-3 h-5 w-5 {activeRoute === 'crm' ? 'text-[#93B4E4]' : 'text-[#7B8FA8]'}"
                    />
                    Sales CRM
                  </button>
                </div>
              {/if}

              <!-- Help Videos Button - Only for user ID 1 and 2 (Buildzilla admins) -->
              {#if userInfo && (userInfo.id === 1 || userInfo.id === 2)}
                <div class="w-full">
                  <button
                    on:click={() => navigate('/admin/help-videos')}
                    class="group flex items-center px-3 py-2 w-full text-[0.9375rem] font-medium rounded-lg transition-colors border-0
                      {activeRoute === '/admin/help-videos' ? 'bg-white/[0.12] border-l-[3px] border-l-[#5B8AC4] text-white' : 'text-gray-200 border-l-[3px] border-l-transparent hover:bg-white/[0.15] hover:text-white hover:border-l-[#5B8AC4]/50'}"
                  >
                    <Icon
                      name="help"
                      className="mr-3 h-5 w-5 {activeRoute === '/admin/help-videos' ? 'text-[#93B4E4]' : 'text-[#7B8FA8]'}"
                    />
                    Help Videos
                  </button>
                </div>
              {/if}

              <!-- Manage Users Button - Only for user ID 1 and 2 -->
              {#if userInfo && (userInfo.id === 1 || userInfo.id === 2)}
                <div class="w-full">
                  <button
                    on:click={() => navigate('/manage-users')}
                    class="group flex items-center px-3 py-2 w-full text-[0.9375rem] font-medium rounded-lg transition-colors border-0
                      {activeRoute === '/manage-users' ? 'bg-white/[0.12] border-l-[3px] border-l-[#5B8AC4] text-white' : 'text-gray-200 border-l-[3px] border-l-transparent hover:bg-white/[0.15] hover:text-white hover:border-l-[#5B8AC4]/50'}"
                  >
                    <Icon 
                      name="users" 
                      className="mr-3 h-5 w-5 {activeRoute === '/manage-users' ? 'text-[#93B4E4]' : 'text-[#7B8FA8]'}" 
                    />
                    Manage Users
                  </button>
                </div>
              {/if}

            </div>
          </nav>
        </div>

        {#if !$user?.coveredByOrganisation && (!$user?.subscription?.status || $user?.subscription?.status === 'trial')}
          <div class="flex-shrink-0 text-center pb-1">
            <span class="text-[0.75rem] text-gray-200">Free Trial</span>
          </div>
        {/if}
        <!-- Home, Help and Settings Buttons - Icons Only, Centered -->
        <div class="flex-shrink-0 border-t border-white/10 py-2.5 flex justify-center gap-5">
          <button 
            on:click={() => navigate('/dashboard')} 
            class="p-2.5 rounded-lg bg-white/[0.06] border border-white/[0.12] hover:bg-white/[0.12] hover:border-white/[0.2] transition-colors focus:outline-none shadow-[0_2px_8px_rgba(0,0,0,0.25)]"
            aria-label="Home"
          >
            <Icon 
              name="home" 
              className="h-[1.125rem] w-[1.125rem] text-gray-200" 
            />
          </button>
          <button 
            on:click={() => navigate('/settings')} 
            class="p-2.5 rounded-lg bg-white/[0.06] border border-white/[0.12] hover:bg-white/[0.12] hover:border-white/[0.2] transition-colors focus:outline-none shadow-[0_2px_8px_rgba(0,0,0,0.25)]"
            aria-label="Settings"
          >
            <Icon 
              name="settings" 
              className="h-[1.125rem] w-[1.125rem] text-gray-200" 
            />
          </button>
          <button 
            on:click={() => navigate('/help')} 
            class="p-2.5 rounded-lg bg-white/[0.06] border border-white/[0.12] hover:bg-white/[0.12] hover:border-white/[0.2] transition-colors focus:outline-none shadow-[0_2px_8px_rgba(0,0,0,0.25)]"
            aria-label="Help Center"
          >
            <Icon 
              name="help" 
              className="h-[1.125rem] w-[1.125rem] text-gray-200" 
            />
          </button>
        </div>
        
        <!-- User Account Details -->
        {#if userInfo}
          <div class="flex-shrink-0 border-t border-white/10 px-3 py-2.5">
            <div class="rounded-md bg-[#111827] px-3 py-3 text-[0.75rem] text-gray-400 space-y-3">
              {#if !isQuickBooks && xeroOrganisationName}
                <div>
                  <div class="font-medium text-gray-200 mb-0.5">Xero Account:</div>
                  <div class="text-gray-400">{xeroOrganisationName}</div>
                </div>
              {/if}
              <div>
                <div class="font-medium text-gray-200 mb-0.5">Jobtread Organisation:</div>
                <div>{jobtreadOrgName || 'Not Connected'}</div>
              </div>
            </div>
          </div>
        {/if}
        
        <div class="flex-shrink-0 flex border-t border-white/10 px-3 py-2">
          <button on:click={handleLogout}
                  class="flex-shrink-0 w-full group flex items-center px-3 py-1.5 text-[0.8125rem] font-medium rounded-lg text-gray-200 hover:bg-white/[0.15] hover:text-white transition-colors focus:outline-none border-0">
            <Icon name="logout" className="mr-2.5 h-4 w-4 text-gray-200 group-hover:text-white" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="flex flex-col w-0 min-w-0 flex-1 overflow-hidden">
    <div class="md:hidden flex items-center justify-between pl-1 pr-3 sm:pl-3 py-1 border-b border-gray-200 bg-white">
      <!-- On mobile the sidebar is replaced by the dashboard menu; this button jumps back there. -->
      <button on:click={() => navigate('/dashboard')}
              class="-ml-0.5 h-12 inline-flex items-center gap-2 px-2 rounded-md text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#5B8AC4]"
              aria-label="Go to main menu">
        <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
        <span class="text-sm font-medium">Menu</span>
      </button>
      <!-- Buildzilla logo mark -->
      <button on:click={() => navigate('/dashboard')} class="flex items-center focus:outline-none" aria-label="Buildzilla home">
        <img src="/images/buildzilla-mark.png" alt="Buildzilla" class="h-7 w-auto" />
      </button>
    </div>
    <main class="content-main min-w-0 flex-1 relative z-0 overflow-y-auto focus:outline-none" tabindex="0">
      <TrialWarningBanner />
      <slot></slot>
    </main>
  </div>
  
  <!-- Session expired modal -->
  {#if showSessionExpiredModal}
    <div class="fixed z-50 inset-0 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Session Expired
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    Your session has expired. Please log in again to continue.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              type="button" 
              on:click={redirectToLogin}
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#4B6A9B] text-base font-medium text-white hover:bg-[#3d5880] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5B8AC4] sm:ml-3 sm:w-auto sm:text-sm"
            >
              Log in
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}

  {#if showOnboardingWizard}
    <OnboardingWizard on:close={() => { showOnboardingWizard = false; fetchUserInfo().then(continueStartup); }} />
  {/if}

  {#if showCombinedUiNotification}
    <CombinedUiNotification on:dismiss={proceedAfterNotification} />
  {/if}
</div>

<style>
  :global(.sidebar-gradient) {
    background: linear-gradient(170deg, #151b2b 0%, #1e2740 20%, #334163 100%);
  }
  :global(.sidebar-gradient *) {
    transform: translateY(-1px);
  }
</style>
